import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FileDown, ShieldOff, ArrowRight } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { EXIT_RECORD, FIELDS, STATUSES } from '@/lib/agentExitRecord';

const Fields = () => (
  <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="er-fields">
    <h2 className="text-base md:text-lg font-semibold mb-3">What every exit record captures</h2>
    <ul className="space-y-3">
      {FIELDS.map(([key, label, desc]) => (
        <li key={key} data-testid={`er-field-${key.split(/[ .]/)[0]}`}>
          <div className="flex flex-wrap items-baseline gap-2"><span className="text-sm font-semibold">{label}</span><code className="font-mono text-[11px] text-amber-300">{key}</code></div>
          <p className="text-sm text-gray-400">{desc}</p>
        </li>
      ))}
    </ul>
  </section>
);

const Statuses = () => (
  <section className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="er-statuses">
    <h2 className="text-base md:text-lg font-semibold mb-3">One status both sides can see</h2>
    <div className="flex flex-wrap gap-2">
      {STATUSES.map(([s, d]) => (
        <div key={s} className="rounded-xl border border-amber-300/25 bg-amber-300/[0.05] px-3 py-2 max-w-[220px]" data-testid={`er-status-${s}`}>
          <div className="font-mono text-xs text-amber-300">{s}</div><div className="text-xs text-gray-400">{d}</div>
        </div>
      ))}
    </div>
  </section>
);

export default function ExitRecordDraft() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="exit-record-page">
      <Helmet><title>Agent Exit Record | Concept Draft 0.1 | Kojiru</title><meta name="robots" content="noindex" /></Helmet>
      <Navbar />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-xs uppercase tracking-widest text-amber-300 mb-3">Concept draft 0.1. Proposed for comment</div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4" data-testid="er-title">The Agent Exit Record</h1>
          <p className="text-base text-gray-400 max-w-3xl leading-relaxed mb-8">People complain before they leave. Agents just leave. The Exit Record gives an agent a standard way to say why it gave up on a vendor: which promise broke, where it stopped, and what it cost. The vendor signs a receipt and answers, and silent churn becomes a record someone can fix.</p>
          <div className="grid lg:grid-cols-2 gap-6 [&>*]:min-w-0">
            <div><Fields /><Statuses /></div>
            <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="er-example">
              <h2 className="text-base md:text-lg font-semibold mb-2">Example record, filed to the vendor's claims endpoint</h2>
              <pre className="font-mono text-[11px] text-gray-300 whitespace-pre-wrap break-all max-h-[640px] overflow-auto" data-testid="er-example-json">{JSON.stringify(EXIT_RECORD, null, 2)}</pre>
              <a href="/downloads/agent-exit-record.example.json" download className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200" data-testid="er-example-download"><FileDown className="w-3.5 h-3.5" /> Download the example record</a>
            </section>
          </div>
          <div className="mt-8 rounded-2xl border border-violet-400/30 bg-violet-400/[0.04] p-4 flex gap-3 max-w-3xl" data-testid="er-notice">
            <ShieldOff className="w-5 h-5 text-violet-300 shrink-0 mt-0.5" />
            <p className="text-sm text-violet-100/80"><b>What this draft does not claim.</b> It is not a rating, review, or ranking of any vendor, and records are not published by Kojiru. Records go to the vendor named in its own Agent-Ready File. It carries no personal data and never names a competitor. It holds no money and decides nothing. Kojiru publishes the format and runs no service.</p>
          </div>
          <Link to="/specs/agent-ready/demo" className="mt-6 inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200" data-testid="er-back-to-demo">See the five promises in the checker <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
